import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonBase,
  Collapse,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowForward,
  Close,
  Download,
  ExpandLess,
  ExpandMore,
  OpenInNew,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import {
  Block,
  BlockLink,
  CardsBlock,
  CtaBlock,
  DownloadsBlock,
  FaqBlock,
  ImageBlock,
  LinksBlock,
  ListBlock,
  QuoteBlock,
  TeasersBlock,
  TextBlock,
  TopicItem,
  TopicsBlock,
} from '../../../corelogic/model/blocks';
import { UPLOADS } from '../../../content/uploads.generated';
import { RichText } from '../../../shared/text/RichText';
import { useLocalizedPath } from '../../../shared/hooks/useLocalizedPath';
import { useLanguage } from '../../../shared/contexts/LanguageContext';
import {
  accentTextColor,
  getCardStyles,
  getOnBrandButtonStyles,
  getSecondaryButtonStyles,
  textMeasure,
  topicTileStates,
} from '../../../shared/styles';
import { Section, SectionHeading, CardGrid } from '../PageSection';
import { ContentIcon } from '../ContentIcon';

/**
 * Renders CMS-authored blocks with the same section vocabulary the
 * hand-written pages use, so a page assembled in the CMS is visually
 * indistinguishable from one written in code.
 *
 * Two rules hold for every block and are not editable, on purpose:
 *  - the page's h1 belongs to the page header, so blocks start at h2;
 *  - a block on the brand tone inherits its colour rather than picking one,
 *    because the accent colour has no contrast on filled CI blue.
 */

/** A button whose target may be internal or external. */
const BlockButton: React.FC<{ link: BlockLink; onBrand: boolean }> = ({ link, onBrand }) => {
  const theme = useTheme();
  const lp = useLocalizedPath();
  const sx = onBrand ? getOnBrandButtonStyles() : getSecondaryButtonStyles(theme);

  if (link.url.startsWith('/')) {
    return (
      <Button component={Link} to={lp(link.url)} sx={sx} endIcon={<ArrowForward />}>
        {link.label}
      </Button>
    );
  }

  return (
    <Button
      component="a"
      href={link.url}
      target={link.url.startsWith('#') ? undefined : '_blank'}
      rel={link.url.startsWith('#') ? undefined : 'noopener noreferrer'}
      sx={sx}
      endIcon={<ArrowForward />}
    >
      {link.label}
    </Button>
  );
};

/** Paragraph text, inline-formatted, in the muted body colour. */
// The measure applies whatever width the editor gave the block: `lg` decides
// how far left the block starts, not how long its lines may run.
const Paragraphs: React.FC<{ items: string[]; inheritColor: boolean; centred?: boolean }> = ({
  items,
  inheritColor,
  centred = false,
}) => (
  <>
    {items.map((paragraph, index) => (
      <Typography
        key={index}
        variant="body1"
        sx={{
          mb: 2.5,
          color: inheritColor ? 'inherit' : 'text.secondary',
          ...textMeasure,
          mx: centred ? 'auto' : 0,
          '&:last-of-type': { mb: 0 },
        }}
      >
        <RichText text={paragraph} />
      </Typography>
    ))}
  </>
);

const TextBlockView: React.FC<{ block: TextBlock; headingId: string }> = ({ block, headingId }) => {
  const onBrand = block.tone === 'brand';

  return (
    <Section
      tone={block.tone}
      maxWidth={block.width}
      id={block.anchor}
      labelledBy={block.title ? headingId : undefined}
    >
      <Box sx={{ textAlign: block.align }}>
        {block.title && (
          <SectionHeading
            id={headingId}
            eyebrow={block.eyebrow}
            title={block.title}
            align={block.align}
            inheritColor={onBrand}
          />
        )}
        <Paragraphs
          items={block.paragraphs}
          inheritColor={onBrand}
          centred={block.align === 'center'}
        />
      </Box>
    </Section>
  );
};

const CardsBlockView: React.FC<{ block: CardsBlock; headingId: string }> = ({ block, headingId }) => {
  const theme = useTheme();
  // On a tinted section the plain card blends in; the contrast card does not.
  const variant = block.tone === 'default' ? 'outlined' : 'contrast';

  return (
    <Section
      tone={block.tone}
      id={block.anchor}
      labelledBy={block.title ? headingId : undefined}
    >
      {block.title && (
        <SectionHeading
          id={headingId}
          title={block.title}
          description={block.description}
          align={block.align}
          inheritColor={block.tone === 'brand'}
        />
      )}
      <CardGrid min={260}>
        {block.items.map((item, index) => (
          <Paper key={index} elevation={0} sx={getCardStyles(theme, variant)}>
            <Typography variant="h4" component="h3" sx={{ mb: 1.5 }}>
              {item.title}
            </Typography>
            {item.description && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <RichText text={item.description} />
              </Typography>
            )}
          </Paper>
        ))}
      </CardGrid>
    </Section>
  );
};

const ListBlockView: React.FC<{ block: ListBlock; headingId: string }> = ({ block, headingId }) => {
  const onBrand = block.tone === 'brand';

  return (
    <Section
      tone={block.tone}
      maxWidth={block.width}
      id={block.anchor}
      labelledBy={block.title ? headingId : undefined}
    >
      {block.title && (
        <SectionHeading
          id={headingId}
          title={block.title}
          description={block.description}
          inheritColor={onBrand}
        />
      )}
      <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'grid', gap: 2 }}>
        {block.items.map((item, index) => (
          <Box
            component="li"
            key={index}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: 2,
              alignItems: 'start',
            }}
          >
            {/* A dot rather than a checkmark: these are statements, not tasks. */}
            <Box
              aria-hidden
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                mt: '0.6em',
                backgroundColor: onBrand ? 'currentColor' : 'primary.main',
                opacity: onBrand ? 0.8 : 1,
              }}
            />
            <Typography variant="body1" sx={{ color: onBrand ? 'inherit' : 'text.secondary' }}>
              <RichText text={item} />
            </Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
};

const TeasersBlockView: React.FC<{ block: TeasersBlock; headingId: string }> = ({
  block,
  headingId,
}) => {
  const onBrand = block.tone === 'brand';

  return (
    <Section tone={block.tone} id={block.anchor} labelledBy={block.title ? headingId : undefined}>
      {block.title && <SectionHeading id={headingId} title={block.title} inheritColor={onBrand} />}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: block.items.length === 1 ? '1fr' : '1fr 1fr' },
          gap: { xs: 5, md: 6 },
        }}
      >
        {block.items.map((item, index) => (
          <Box key={index}>
            <Typography variant="h3" component="h3" sx={{ mb: 2, color: 'inherit' }}>
              {item.title}
            </Typography>
            {item.description && (
              <Typography
                variant="body1"
                sx={{ color: onBrand ? 'inherit' : 'text.secondary', mb: item.link ? 3 : 0 }}
              >
                <RichText text={item.description} />
              </Typography>
            )}
            {item.link && <BlockButton link={item.link} onBrand={onBrand} />}
          </Box>
        ))}
      </Box>
    </Section>
  );
};

/**
 * A heading, a line of text and a button, any of which may be missing.
 *
 * Without a heading it is a button standing on its own - which is what a page
 * that carries its title above the blocks needs, and what an editor who cleared
 * the heading field asked for. The section then has no name of its own rather
 * than being labelled by a heading that is not there, and it closes up: a
 * button under the page's opening belongs to the line that explains it, so it
 * keeps the tighter rhythm instead of standing a section's width away from it.
 */
const CtaBlockView: React.FC<{ block: CtaBlock; headingId: string }> = ({ block, headingId }) => {
  const onBrand = block.tone === 'brand';

  return (
    <Section
      tone={block.tone}
      id={block.anchor}
      labelledBy={block.title ? headingId : undefined}
      spacing={block.title ? 'normal' : 'tight'}
    >
      <Box sx={{ textAlign: 'center' }}>
        {block.title && (
          <Typography id={headingId} variant="h2" component="h2" sx={{ mb: 2, color: 'inherit' }}>
            {block.title}
          </Typography>
        )}
        {block.description && (
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: onBrand ? 'inherit' : 'text.secondary',
              opacity: onBrand ? 0.95 : 1,
              ...textMeasure,
              mx: 'auto',
            }}
          >
            <RichText text={block.description} />
          </Typography>
        )}
        {block.link && <BlockButton link={block.link} onBrand={onBrand} />}
      </Box>
    </Section>
  );
};

const QuoteBlockView: React.FC<{ block: QuoteBlock }> = ({ block }) => {
  const theme = useTheme();
  const onBrand = block.tone === 'brand';

  return (
    <Section tone={block.tone} maxWidth={block.width} id={block.anchor}>
      <Box
        component="figure"
        sx={{
          m: 0,
          pl: { xs: 3, md: 4 },
          borderLeft: '3px solid',
          borderColor: onBrand ? 'currentColor' : theme.palette.primary.main,
        }}
      >
        <Typography
          component="blockquote"
          variant="h4"
          sx={{ m: 0, fontWeight: 400, lineHeight: 1.5, color: 'inherit' }}
        >
          <RichText text={block.text} />
        </Typography>
        {block.source && (
          <Typography
            component="figcaption"
            variant="body2"
            sx={{ mt: 2.5, color: onBrand ? 'inherit' : 'text.secondary', opacity: onBrand ? 0.9 : 1 }}
          >
            {block.source}
          </Typography>
        )}
      </Box>
    </Section>
  );
};

/**
 * The width the layout gives an image, so the browser can pick a version
 * before it knows how wide anything is. Matches the container widths in
 * PageSection: full width on a phone, capped by the column on a large screen.
 */
const IMAGE_SIZES = {
  sm: '(min-width: 640px) 600px, 100vw',
  md: '(min-width: 900px) 900px, 100vw',
  lg: '(min-width: 1200px) 1152px, 100vw',
  xl: '(min-width: 1536px) 1624px, 100vw',
};

const ImageBlockView: React.FC<{ block: ImageBlock }> = ({ block }) => {
  // Generated by scripts/optimise-uploads.js. Absent for an image that was
  // added after the last build, or one linked from elsewhere: then the
  // original is served as it always was, which is worse but never broken.
  const upload = UPLOADS[block.src];

  return (
  <Section tone={block.tone} maxWidth={block.width} id={block.anchor}>
    <Box component="figure" sx={{ m: 0 }}>
      {/* Plain elements, not Box: MUI reads `width` and `height` as style
          props and swallows them, and it is the attributes that let the
          browser reserve the space. */}
      <picture>
        {upload && (
          <source
            type="image/webp"
            srcSet={upload.variants
              .map((variant) => `${encodeURI(variant.src)} ${variant.width}w`)
              .join(', ')}
            sizes={IMAGE_SIZES[block.width]}
          />
        )}
        <img
          src={block.src}
          alt={block.alt}
          loading="lazy"
          decoding="async"
          // Reserving the space keeps the text below from jumping once the
          // picture arrives - the kind of movement that costs a reader their
          // place, and more than that for anyone sensitive to motion.
          width={upload?.width}
          height={upload?.height}
          style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 12 }}
        />
      </picture>
      {block.caption && (
        <Typography
          component="figcaption"
          variant="body2"
          sx={{ mt: 1.5, color: block.tone === 'brand' ? 'inherit' : 'text.secondary' }}
        >
          <RichText text={block.caption} />
        </Typography>
      )}
    </Box>
  </Section>
  );
};

/** "PDF", "DOCX" … taken from the file name, or nothing if it has no suffix. */
const fileType = (file: string): string | undefined => {
  const match = /\.([a-z0-9]{1,8})(?:[?#]|$)/i.exec(file);
  return match ? match[1].toUpperCase() : undefined;
};

/**
 * Documents to download, one row each.
 *
 * The file type is shown rather than left to the reader to guess from the URL:
 * knowing that a link leads to a 40-page PDF, and not to another web page, is
 * the difference between a deliberate click and a surprise for someone reading
 * on a phone with little energy to spare.
 *
 * The rows sit on their own surface at every tone, so the document titles keep
 * the normal text colour instead of inheriting white on a CI-blue section.
 */
const DownloadsBlockView: React.FC<{ block: DownloadsBlock; headingId: string }> = ({
  block,
  headingId,
}) => {
  const theme = useTheme();
  // Same rule as the cards: a plain card disappears into a tinted section.
  const variant = block.tone === 'default' ? 'outlined' : 'contrast';

  return (
    <Section
      tone={block.tone}
      maxWidth={block.width}
      id={block.anchor}
      labelledBy={block.title ? headingId : undefined}
    >
      {block.title && (
        <SectionHeading
          id={headingId}
          title={block.title}
          description={block.description}
          inheritColor={block.tone === 'brand'}
        />
      )}
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 1.5 }}>
        {block.items.map((item, index) => {
          const type = fileType(item.file);

          return (
            <Box component="li" key={index}>
              <Box
                component="a"
                href={item.file}
                // A plain anchor, not a router link: this address is a file,
                // not a route, so it has to leave the application.
                //
                // No `download` attribute on purpose. With it, the browser
                // saves the file and shows nothing; without it, a PDF opens in
                // the browser's own viewer and can still be saved from there.
                // Reading before downloading is the commoner wish, and it is
                // the gentler default on a phone.
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  ...getCardStyles(theme, variant),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2.5,
                  p: { xs: 2.5, md: 3 },
                  textDecoration: 'none',
                  color: 'text.primary',
                  '&:hover .download-label, &:focus-visible .download-label': {
                    textDecoration: 'underline',
                  },
                }}
              >
                <Download aria-hidden sx={{ color: 'primary.main', flexShrink: 0 }} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    className="download-label"
                    variant="h4"
                    component="span"
                    sx={{ display: 'block', fontSize: '1.0625rem' }}
                  >
                    {item.label}
                  </Typography>
                  {/* Plain text, not RichText: the whole row is the link, and
                      a link inside a link is neither valid nor usable. The
                      hint is one line about the document, so nothing is lost. */}
                  {item.description && (
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}
                    >
                      {item.description}
                    </Typography>
                  )}
                </Box>
                {type && (
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ color: 'text.secondary', flexShrink: 0, fontWeight: 600 }}
                  >
                    {type}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Section>
  );
};

/**
 * A list of links.
 *
 * Rendered as rows rather than buttons: a button says "do this one thing", and
 * a list of eight buttons says nothing. Rows stay readable at any length, and
 * each carries an optional line saying where the link leads - which matters
 * most for the external ones, where following it means leaving the site.
 */
const LinksBlockView: React.FC<{ block: LinksBlock; headingId: string }> = ({
  block,
  headingId,
}) => {
  const theme = useTheme();
  const lp = useLocalizedPath();
  const variant = block.tone === 'default' ? 'outlined' : 'contrast';

  return (
    <Section
      tone={block.tone}
      maxWidth={block.width}
      id={block.anchor}
      labelledBy={block.title ? headingId : undefined}
    >
      {block.title && (
        <SectionHeading
          id={headingId}
          title={block.title}
          description={block.description}
          inheritColor={block.tone === 'brand'}
        />
      )}
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 1.5 }}>
        {block.items.map((item, index) => {
          const internal = item.url.startsWith('/');
          const samePage = item.url.startsWith('#');
          const rowStyles = {
            ...getCardStyles(theme, variant),
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            p: { xs: 2.5, md: 3 },
            textDecoration: 'none',
            color: 'text.primary',
            '&:hover .link-label, &:focus-visible .link-label': { textDecoration: 'underline' },
          };
          const body = (
            <>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                  className="link-label"
                  variant="h4"
                  component="span"
                  sx={{ display: 'block', fontSize: '1.0625rem' }}
                >
                  {item.label}
                </Typography>
                {/* Plain text, not RichText: the whole row is already a link,
                    and a link inside a link is neither valid nor usable. */}
                {item.description && (
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}
                  >
                    {item.description}
                  </Typography>
                )}
              </Box>
              {internal || samePage ? (
                <ArrowForward aria-hidden sx={{ color: 'primary.main', flexShrink: 0 }} />
              ) : (
                <OpenInNew aria-hidden sx={{ color: 'primary.main', flexShrink: 0 }} />
              )}
            </>
          );

          return (
            <Box component="li" key={index}>
              {internal ? (
                <Box component={Link} to={lp(item.url)} sx={rowStyles}>
                  {body}
                </Box>
              ) : (
                <Box
                  component="a"
                  href={item.url}
                  target={samePage ? undefined : '_blank'}
                  rel={samePage ? undefined : 'noopener noreferrer'}
                  sx={rowStyles}
                >
                  {body}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Section>
  );
};

const FaqBlockView: React.FC<{ block: FaqBlock; headingId: string }> = ({ block, headingId }) => {
  const theme = useTheme();

  return (
    <Section
      tone={block.tone}
      maxWidth={block.width}
      id={block.anchor}
      labelledBy={block.title ? headingId : undefined}
    >
      {block.title && (
        <SectionHeading id={headingId} title={block.title} description={block.description} />
      )}
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {block.items.map((item, index) => (
          <Accordion
            key={index}
            elevation={0}
            disableGutters
            // No animation: expanding content that slides is a motion trigger.
            slotProps={{ transition: { timeout: 0 } }}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              backgroundColor: 'background.paper',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1 }}>
              <Typography variant="h4" component="h3" sx={{ fontSize: '1.0625rem' }}>
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                <RichText text={item.answer} />
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Section>
  );
};

/**
 * One tile of a wide topics block: a heading, a line that stays visible, and a
 * short text folded away behind a control.
 *
 * The link sits *inside* the folded part whenever there is a text, and outside
 * it when there is none. A tile with a text is something to read first and
 * follow afterwards, so the closed tile carries one control and not two; a
 * tile without a text is a signpost, and hiding the only thing it does behind
 * a control that reveals nothing else would be a joke at the reader's expense.
 *
 * Folding, not linking away, because the answer to "what is PEM?" is three
 * sentences long. Making that a page of its own would cost a page load and a
 * way back for something a reader takes in on the spot.
 *
 * The control sits above the folded text rather than under it. Under it, a
 * tile that has been read has to be scrolled back down past to be closed
 * again.
 */
const TopicTile: React.FC<{
  item: TopicItem;
  panelId: string;
  headingId: string;
}> = ({ item, panelId, headingId }) => {
  const theme = useTheme();
  const { t } = useLanguage();
  const [open, setOpen] = React.useState(false);
  const foldable = item.text !== undefined;

  return (
    <Paper
      elevation={0}
      sx={{
        ...getCardStyles(theme),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1.5,
        // Its own height, not the row's. Grid items stretch by default, so
        // opening one tile would drag its neighbours to the same height and
        // leave them standing half empty - the reader would see three tiles
        // grow to answer a question they asked of one.
        height: 'auto',
      }}
    >
      {/* The symbol sits above the title, not beside it: a tile is read
          from the top down, and a symbol in the first column would push
          every title in the grid onto a different left edge. Decorative -
          it stands next to the words it is about, so naming it would only
          make a screen reader read the tile's heading twice. */}
      {item.icon && <ContentIcon name={item.icon} size={32} sx={{ mb: 0.5 }} />}

      <Typography id={headingId} variant="h4" component="h3" sx={{ fontSize: '1.125rem' }}>
        {item.title}
      </Typography>

      {/* The reading size, not a step below it: this line introduces the text
          that unfolds under it, and a lead set smaller than the body it leads
          into reads as a caption that lost its picture. The hierarchy is
          carried by colour instead - this line in the text colour, the folded
          text muted under it. */}
      {item.summary !== undefined && (
        <Typography variant="body1" sx={{ color: 'text.primary' }}>
          <RichText text={item.summary} />
        </Typography>
      )}

      {foldable && (
        <>
          <ButtonBase
            onClick={() => setOpen((wasOpen) => !wasOpen)}
            // No ripple: it is the one moving thing MUI adds by default, and
            // this site does not move.
            disableRipple
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            sx={{
              alignSelf: 'flex-start',
              gap: 0.5,
              px: 1,
              py: 0.75,
              ml: -1,
              borderRadius: 1.5,
              color: accentTextColor(theme),
              fontWeight: 600,
              fontSize: '0.9375rem',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {open ? t('topics.close') : t('topics.open')}
            {/* Two icons rather than one rotating: a rotation is movement,
                and the state has to be legible without any. */}
            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </ButtonBase>

          <Collapse in={open} unmountOnExit timeout={0}>
            <Box id={panelId} role="region" aria-labelledby={headingId} sx={{ pt: 0.5 }}>
              {/* Line breaks as typed. The field is a text area and the text
                  in it is often a short list - two organisations, three
                  pointers - so a blank line an editor put in has to survive
                  rather than collapse into one run-on paragraph. */}
              {item.text && (
                <Typography variant="body1" sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}>
                  <RichText text={item.text} />
                </Typography>
              )}
              {item.link && (
                <Box sx={{ mt: 2.5 }}>
                  <BlockButton link={item.link} onBrand={false} />
                </Box>
              )}
            </Box>
          </Collapse>
        </>
      )}

      {!foldable && item.link && <BlockButton link={item.link} onBrand={false} />}
    </Paper>
  );
};

/**
 * One name on the shelf: the whole tile is the control, and it carries a title
 * and nothing else.
 *
 * What it opens does not open *inside* it - it stands in one panel below the
 * tile's row, so the shelf keeps its shape while it is read. A tile that grew
 * to hold its own text would push its neighbours into other columns and change
 * the meaning of the reader's scroll position, which is movement of the kind
 * the rest of this site goes out of its way to avoid.
 *
 * Closed and open are the same box, then. Only the fill says which one is
 * being read.
 *
 * A tile with nothing behind it stays a plain box rather than a target that
 * answers a click with nothing: it carries its link and is a signpost.
 */
const TopicTileButton: React.FC<{
  item: TopicItem;
  panelId: string;
  headingId: string;
  open: boolean;
  onToggle: () => void;
  buttonRef?: React.Ref<HTMLButtonElement>;
}> = ({ item, panelId, headingId, open, onToggle, buttonRef }) => {
  const theme = useTheme();
  const tileStates = topicTileStates(theme);
  const foldable = item.text !== undefined || item.summary !== undefined;

  return (
    <Paper
      elevation={0}
      sx={{
        ...getCardStyles(theme),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        height: 'auto',
        // The padding belongs to the button inside, not to the box: a tile
        // that is one target has to take the click at its edge too, and
        // padding out here would leave a dead border around it.
        p: 0,
        gap: 0,
        // Blue at the edge, so a shelf of tiles reads as a set of things to
        // press and not as a set of boxes text happens to sit in. Filled while
        // its panel is open, which is the only mark saying which name the
        // panel below belongs to.
        border: `1.5px solid ${tileStates.border}`,
        backgroundColor: open ? tileStates.open : 'transparent',
        // The pointer fills the tile it is over, the way the mock-up has it -
        // a shelf of names where the one under the cursor is the one that is
        // about to open.
        '&:hover': foldable ? { backgroundColor: open ? tileStates.open : tileStates.hover } : {},
      }}
    >
      <ButtonBase
        ref={buttonRef}
        onClick={onToggle}
        disabled={!foldable}
        // No ripple: it is the one moving thing MUI adds by default, and this
        // site does not move.
        disableRipple
        aria-expanded={foldable ? open : undefined}
        aria-controls={open ? panelId : undefined}
        sx={{
          // Every tile is the same box whether its title runs to one line or
          // two: a shelf of tiles that are all a different height by an
          // accident of wording does not read as a shelf. A minimum rather
          // than a height, so a long title is never cut.
          minHeight: { xs: 84, md: 96 },
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.5, md: 2 },
          borderRadius: 3,
          // Centred, because the tile is a label on a button and not the first
          // line of something to read.
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          id={headingId}
          variant="h4"
          component="h3"
          sx={{ fontSize: { xs: '0.9375rem', md: '1rem' } }}
        >
          {item.title}
        </Typography>
      </ButtonBase>

      {!foldable && item.link && (
        <Box sx={{ px: { xs: 2, md: 2.5 }, pb: { xs: 2, md: 2.5 } }}>
          <BlockButton link={item.link} onBrand={false} />
        </Box>
      )}
    </Paper>
  );
};

/**
 * The one panel the compact shelf opens into, standing across the full width
 * below the row of the tile that was pressed.
 *
 * Full width because the answer is then read at a proper measure instead of
 * down a column a fifth of the page wide. Below the row rather than in place
 * of the tile, so nothing above it moves and the shelf can be clicked through:
 * pressing another name swaps what is in the panel instead of asking for a
 * close first, which is what a library is for.
 *
 * The title stands in it again. On a wide screen the panel sits a row under
 * the tile that opened it and up to four names across from it, and a text with
 * no name over it is a text about nothing in particular. Not a heading,
 * though: the tile's own heading is what names this region, and a second one
 * would put the same topic into the page outline twice.
 */
const TopicPanel: React.FC<{
  item: TopicItem;
  id: string;
  labelledBy: string;
  onClose: () => void;
}> = ({ item, id, labelledBy, onClose }) => {
  const theme = useTheme();
  const { t } = useLanguage();
  const tileStates = topicTileStates(theme);
  const panelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // `nearest` scrolls only when it has to, and on a phone it has to: two
    // tiles to a row means the panel can open just past the bottom edge. No
    // `smooth` - a jump rather than a ride, like everything else here.
    panelRef.current?.scrollIntoView({ block: 'nearest' });
  }, [item]);

  return (
    <Box
      ref={panelRef}
      id={id}
      role="region"
      aria-labelledby={labelledBy}
      sx={{
        gridColumn: '1 / -1',
        position: 'relative',
        borderRadius: 3,
        // The tile's own fill and edge, carried down: that pairing is what
        // says the panel belongs to the one tile standing filled above it.
        border: `1.5px solid ${tileStates.border}`,
        backgroundColor: tileStates.open,
        px: { xs: 2.5, md: 4 },
        py: { xs: 2.5, md: 3.5 },
      }}
    >
      {/* Pressing the tile again closes it too. This is here for the reader
          who has scrolled down to the end of the text and would otherwise
          have to scroll back up to the shelf to get out. */}
      <IconButton
        onClick={onClose}
        aria-label={t('topics.closePanel')}
        size="small"
        sx={{ position: 'absolute', top: 8, right: 8, color: 'text.secondary' }}
      >
        <Close fontSize="small" />
      </IconButton>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, pr: 4 }}>
        {/* Decorative: it stands next to the name it is about, and naming it
            would only have a screen reader read the topic twice. */}
        {item.icon && <ContentIcon name={item.icon} size={28} />}
        <Typography variant="h4" component="p" sx={{ fontSize: { xs: '1.0625rem', md: '1.25rem' } }}>
          {item.title}
        </Typography>
      </Box>

      {item.summary !== undefined && (
        <Typography variant="body1" sx={{ color: 'text.primary', mb: 1.5, ...textMeasure }}>
          <RichText text={item.summary} />
        </Typography>
      )}

      {/* Line breaks as typed. The field is a text area and the text in it is
          often a short list - two organisations, three pointers - so a blank
          line an editor put in has to survive rather than collapse into one
          run-on paragraph. */}
      {item.text && (
        <Typography
          variant="body1"
          sx={{ color: 'text.secondary', whiteSpace: 'pre-line', ...textMeasure }}
        >
          <RichText text={item.text} />
        </Typography>
      )}

      {item.link && (
        <Box sx={{ mt: 2.5 }}>
          <BlockButton link={item.link} onBrand={false} />
        </Box>
      )}
    </Box>
  );
};

/**
 * How many tiles stand next to each other.
 *
 * Two columns for the wide form, and not as many as fit: three of them cut the
 * line to about thirty characters, which turns a four-sentence answer into a
 * column of fragments - and the point of a wide tile is that the answer is
 * readable where it stands.
 *
 * The compact form is the opposite case. Nothing is standing open in it, so
 * width buys nothing and the row is what does the work: four or five names
 * side by side are a shelf a reader takes in at a glance, which is what a
 * library page has to be before it is anything else.
 */
const TOPIC_COLUMNS = {
  wide: { xs: '1fr', md: '1fr 1fr' },
  compact: {
    xs: '1fr 1fr',
    sm: 'repeat(3, 1fr)',
    md: 'repeat(4, 1fr)',
    lg: 'repeat(5, 1fr)',
  },
} as const;

/**
 * The same column counts as `TOPIC_COLUMNS.compact`, as numbers.
 *
 * The panel goes after the last tile of the row that was pressed, and the
 * markup has to name that tile. CSS grid knows where the rows break; the
 * component does not, so the breakpoint is read back here. The two lists have
 * to be changed together - hence one comment on both.
 */
const useCompactTopicColumns = (): number => {
  const theme = useTheme();
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const sm = useMediaQuery(theme.breakpoints.up('sm'));

  if (lg) return 5;
  if (md) return 4;
  if (sm) return 3;
  return 2;
};

/**
 * The shelf: names in a grid, one detail panel below whichever row is open.
 *
 * One panel and not one per tile, so that pressing a second name is a swap and
 * not a close-then-open, and so that only one topic is ever standing on the
 * page at a time.
 */
const CompactTopicGrid: React.FC<{ items: TopicItem[]; headingId: string }> = ({
  items,
  headingId,
}) => {
  const columns = useCompactTopicColumns();
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const openTile = React.useRef<HTMLButtonElement | null>(null);
  const panelId = `${headingId}-panel`;

  const close = () => {
    // Focus first, while the tile it goes back to is still on the page. Closed
    // from inside the panel, a keyboard reader would otherwise be put down on
    // the document body with the whole page to walk back through.
    openTile.current?.focus();
    setOpenIndex(null);
  };

  // The last tile of the open tile's row - or the last tile there is, when
  // that row is the short one at the end.
  const panelAfter =
    openIndex === null
      ? null
      : Math.min(Math.floor(openIndex / columns) * columns + columns - 1, items.length - 1);

  return (
    <Box
      // Escape closes, from the tile or from anywhere inside the panel. It is
      // what the key does everywhere else, and this panel is large enough to
      // be looked at as something to get out of.
      onKeyDown={(event) => {
        if (event.key === 'Escape' && openIndex !== null) close();
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: TOPIC_COLUMNS.compact,
        gap: { xs: 2, md: 2.5 },
        // `start` keeps each tile at its own height rather than the row's.
        alignItems: 'start',
      }}
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>
          <TopicTileButton
            item={item}
            headingId={`${headingId}-tile-${index}`}
            panelId={panelId}
            open={openIndex === index}
            onToggle={() => setOpenIndex((current) => (current === index ? null : index))}
            buttonRef={openIndex === index ? openTile : undefined}
          />
          {openIndex !== null && panelAfter === index && (
            <TopicPanel
              item={items[openIndex]}
              id={panelId}
              labelledBy={`${headingId}-tile-${openIndex}`}
              onClose={close}
            />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

/**
 * A grid of tiles: the wide form folds each one open where it stands, the
 * compact form is a shelf of names with one panel under it.
 *
 * The tiles keep the page's own surface at every tone, like the cards and the
 * download rows do, so a tile's text never inherits white on CI blue.
 */
const TopicsBlockView: React.FC<{ block: TopicsBlock; headingId: string }> = ({
  block,
  headingId,
}) => (
  <Section
    tone={block.tone}
    maxWidth={block.width}
    id={block.anchor}
    labelledBy={block.title ? headingId : undefined}
  >
    {block.title && (
      <SectionHeading
        id={headingId}
        title={block.title}
        description={block.description}
        inheritColor={block.tone === 'brand'}
      />
    )}
    {block.compact ? (
      <CompactTopicGrid items={block.items} headingId={headingId} />
    ) : (
      // `start` keeps each tile at its own height, so opening one does not
      // drag its neighbours up to match.
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: TOPIC_COLUMNS.wide,
          gap: { xs: 2, md: 3 },
          alignItems: 'start',
        }}
      >
        {block.items.map((item, index) => (
          <TopicTile
            key={index}
            item={item}
            headingId={`${headingId}-tile-${index}`}
            panelId={`${headingId}-panel-${index}`}
          />
        ))}
      </Box>
    )}
  </Section>
);

const renderBlock = (block: Block, index: number): React.ReactNode => {
  // Stable per position, and only ever referenced from the same block.
  const headingId = `block-${index}-heading`;

  switch (block.type) {
    case 'text':
      return <TextBlockView key={index} block={block} headingId={headingId} />;
    case 'cards':
      return <CardsBlockView key={index} block={block} headingId={headingId} />;
    case 'list':
      return <ListBlockView key={index} block={block} headingId={headingId} />;
    case 'teasers':
      return <TeasersBlockView key={index} block={block} headingId={headingId} />;
    case 'cta':
      return <CtaBlockView key={index} block={block} headingId={headingId} />;
    case 'quote':
      return <QuoteBlockView key={index} block={block} />;
    case 'image':
      return <ImageBlockView key={index} block={block} />;
    case 'downloads':
      return <DownloadsBlockView key={index} block={block} headingId={headingId} />;
    case 'links':
      return <LinksBlockView key={index} block={block} headingId={headingId} />;
    case 'faq':
      return <FaqBlockView key={index} block={block} headingId={headingId} />;
    case 'topics':
      return <TopicsBlockView key={index} block={block} headingId={headingId} />;
    default:
      // normalizeBlocks has already dropped anything unknown; this is only
      // here so that adding a block type without a view fails the type check.
      return null;
  }
};

export const BlockRenderer: React.FC<{ blocks: Block[] }> = ({ blocks }) => (
  <>{blocks.map(renderBlock)}</>
);
